// Librairies
import { v4 as uuidv4 } from 'uuid';
// Utils
import { planEnqueue, isCreationType, withoutEntities } from './OutboxCompaction';

export const OP_STATUS = { PENDING: 'pending', SENDING: 'sending', FAILED: 'failed', BLOCKED: 'blocked' };
export const LANES = { DATA: 'data', FILES: 'files' };
export const targetKeyOf = ({ entity, id }) => `${entity}:${id}`;

// File des opérations à envoyer. Une opération n'en sort qu'acceptée, écartée ou abandonnée par l'utilisateur, jamais en silence
export const createOutbox = (db, { userId } = {}) => {
    const listeners = new Set();
    const notify = () => listeners.forEach(listener => listener());
    const nowIso = () => new Date(Date.now()).toISOString();
    const byId = (id) => db.outbox.where('id').equals(id);
    const update = async (id, changes) => {
        await byId(id).modify(changes);
        notify();
    };

    const outbox = {
        // Prévient d'un changement de la file (compteurs, écran Synchronisation) ; renvoie la fonction de désabonnement
        subscribe: (listener) => {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },

        // Ajoute une saisie, ou la fond dans l'opération en attente du même élément (merged) ; calcule ses dépendances
        enqueue: async (input) => {
            const targetKey = targetKeyOf(input.target);
            const op = {
                id: uuidv4(), userId, projectId: input.projectId ?? null, lane: input.lane || LANES.DATA,
                type: input.type, target: input.target, targetKey,
                touches: input.touches || [targetKey],
                refs: input.refs || [], cascadeKey: input.cascadeKey || null, softDependents: !!input.softDependents, softWaitFor: input.softWaitFor || [],
                payload: input.payload ?? null, base: input.base ?? null,
                dependsOn: [], softDependsOn: [], status: OP_STATUS.PENDING, attempts: 0, nextAttemptAt: 0, lastError: null,
                capturedAt: input.capturedAt || nowIso(), createdAt: nowIso()
            };
            let result = null, merged = false;
            await db.transaction('rw', db.outbox, async () => {
                // Dépend de toute opération antérieure qui touche la même cible, et de la création des entités référencées (pas d'une
                // création qui ne touche l'élément que comme origine d'une découpe : sa photo ou son action n'a pas besoin du morceau)
                const refKeys = op.refs.map(targetKeyOf);
                const related = (await db.outbox.where('touches').anyOf([...op.touches, ...refKeys]).distinct().toArray())
                    .filter(o => o.touches.some(touch => op.touches.includes(touch))
                        || (isCreationType(o.type) && o.touches.some(touch => refKeys.includes(touch) && !(o.softWaitFor || []).includes(touch))));

                // Attente souple (son échec ou son abandon ne bloque ni ne retire l'opération) : créations en file qui visent ces clés
                // (photos et actions des origines d'une découpe, recopiées par l'API ; photos d'un arbre avant sa replantation),
                // replantation en file de l'élément (ses photos suivantes vont au nouvel arbre) et, pour une replantation, validations
                // d'actions de l'élément (l'API ne replante qu'après l'abattage validé)
                const waitKeys = op.softWaitFor;
                const isReplanting = !!op.payload?.flags?.replanting;
                const softDependencies = waitKeys.length ? await db.outbox.filter(o => (isCreationType(o.type)
                    && (waitKeys.includes(o.cascadeKey) || (o.refs || []).some(ref => waitKeys.includes(targetKeyOf(ref)))))
                    || (o.type === 'element.update' && !!o.payload?.flags?.replanting && waitKeys.includes(o.targetKey))
                    || (isReplanting && o.type === 'occurrences.update' && (o.payload?.occurrences || []).some(occurrence => waitKeys.includes(`element:${occurrence.elementId}`)))).toArray() : [];

                const sameTarget = await db.outbox.where('targetKey').equals(targetKey).sortBy('seq');
                const plan = planEnqueue(sameTarget.filter(o => o.status === OP_STATUS.PENDING && !o.attempts), op);
                const mergeTarget = plan.update && sameTarget.find(o => o.id === plan.update.id);
                // Pas de fusion si une opération plus récente touche l'élément ou crée une entité référencée : la cible partirait avant elle
                // Ni si la saisie apporte une dépendance stricte que la cible n'a pas : son abandon retirerait les saisies plus anciennes
                const addsStrictDependency = !!mergeTarget && related.some(o => o.id !== mergeTarget.id && !o.softDependents && !mergeTarget.dependsOn.includes(o.id));
                if (mergeTarget && !addsStrictDependency && !related.some(o => o.id !== mergeTarget.id && o.seq > mergeTarget.seq)) {
                    const refs = [...mergeTarget.refs, ...op.refs.filter(ref => !mergeTarget.refs.some(existing => targetKeyOf(existing) === targetKeyOf(ref)))];
                    const dependsOn = [...new Set([...mergeTarget.dependsOn, ...related.filter(o => o.id !== mergeTarget.id).map(o => o.id)])];
                    // Attentes souples reprises sauf vers une saisie qui attend déjà la cible (attente circulaire) ; une création n'attend rien
                    // de plus, ni ne reprend la clé, que les saisies suivantes doivent attendre strictement
                    const waitsForTarget = o => [...(o.dependsOn || []), ...(o.softDependsOn || [])].includes(mergeTarget.id);
                    await byId(mergeTarget.id).modify({
                        ...plan.update.changes, refs, dependsOn,
                        ...(!isCreationType(mergeTarget.type) && {
                            softWaitFor: [...new Set([...(mergeTarget.softWaitFor || []), ...waitKeys])],
                            softDependsOn: [...new Set([...(mergeTarget.softDependsOn || []), ...softDependencies.filter(o => o.id !== mergeTarget.id && !waitsForTarget(o)).map(o => o.id)])]
                        })
                    });
                    result = await byId(mergeTarget.id).first();
                    // Opération fusionnée : sur un refus, la façade ne doit pas la retirer, elle porte aussi des saisies plus anciennes
                    merged = true;
                    return;
                }
                op.dependsOn = related.map(o => o.id);
                op.softDependsOn = softDependencies.map(o => o.id);
                await db.outbox.add(op);
                result = op;
            });
            notify();
            return { op: result, merged };
        },

        // Retire les créations jamais envoyées d'éléments supprimés sur l'appareil, avec ce qui en dépend
        cancelLocalCreations: async (targetKeys) => {
            const cancelledKeys = [], removedOps = [];
            await db.transaction('rw', db.outbox, async () => {
                for (const key of targetKeys) {
                    const ops = await db.outbox.where('touches').equals(key).toArray();
                    const creation = ops.find(o => isCreationType(o.type) && o.targetKey === key);
                    if (!creation || creation.status !== OP_STATUS.PENDING || creation.attempts) continue;
                    // Origine d'une découpe ou d'une fusion (une autre création la touche) : elle part, ses photos et actions sont recopiées sur les morceaux
                    if (ops.some(o => o.id !== creation.id && isCreationType(o.type))) continue;
                    const cascade = await db.outbox.filter(o => o.cascadeKey === key).toArray();
                    const toRemove = [...ops.filter(o => o === creation || o.touches.length === 1), ...cascade];
                    await db.outbox.bulkDelete(toRemove.map(o => o.seq));
                    removedOps.push(...toRemove);
                    cancelledKeys.push(key);
                }
            });
            if (cancelledKeys.length) notify();
            return { cancelledKeys, removedOps };
        },

        // Occurrence d'action créée sur l'appareil dans un lot (occurrences.create) jamais envoyé : retirée du lot (le lot disparaît s'il
        // ne contient plus rien), avec les opérations qui ne concernent qu'elle et ses photos (cascadeKey)
        cancelLocalOccurrence: async (localId) => {
            const key = `occurrence:${localId}`;
            const removedOps = [];
            let cancelled = false;
            await db.transaction('rw', db.outbox, async () => {
                const ops = await db.outbox.where('touches').equals(key).toArray();
                const creation = ops.find(o => o.type === 'occurrences.create');
                if (!creation || creation.status !== OP_STATUS.PENDING || creation.attempts) return;
                const elementId = Object.keys(creation.payload.localIds).find(id => creation.payload.localIds[id] === localId);
                const localIds = { ...creation.payload.localIds };
                delete localIds[elementId];
                const elements = creation.payload.creation.elements.filter(element => element.elementId !== elementId);
                // L'élément retiré n'est plus référencé : le lot n'attend plus sa création (son échec ne le bloque plus, son abandon ne lui
                // retire rien)
                if (elements.length) {
                    const remainingIds = elements.map(element => element.elementId);
                    const dependencies = (await Promise.all(creation.dependsOn.map(dependencyId => byId(dependencyId).first()))).filter(Boolean);
                    const unneeded = dependencies.filter(o => (o.type === 'element.create' && !remainingIds.includes(o.target.id))
                        || (o.type === 'element.bulkCreate' && !o.payload.features.some(feature => remainingIds.includes(feature.id)))).map(o => o.id);
                    await byId(creation.id).modify({
                        touches: creation.touches.filter(touch => touch !== key),
                        refs: creation.refs.filter(ref => !(ref.entity === 'element' && ref.id === elementId)),
                        dependsOn: creation.dependsOn.filter(dependencyId => !unneeded.includes(dependencyId)),
                        payload: { ...creation.payload, localIds, creation: { ...creation.payload.creation, elements } }
                    });
                } else {
                    await byId(creation.id).delete();
                    removedOps.push(creation);
                }
                const cascade = await db.outbox.filter(o => o.cascadeKey === key).toArray();
                const toRemove = [...ops.filter(o => o.id !== creation.id && o.touches.length === 1), ...cascade];
                await db.outbox.bulkDelete(toRemove.map(o => o.seq));
                removedOps.push(...toRemove);
                cancelled = true;
            });
            if (cancelled) notify();
            return { cancelled, removedOps };
        },

        // Modification puis suppression : retire les modifications des éléments jamais tentées ou en échec (la suppression suffit)
        cancelPendingUpdates: async (targetKeys) => {
            // Sauf celles d'une origine de découpe en file : si le morceau est abandonné, l'origine reste avec ses modifications
            const originKeys = new Set((await db.outbox.filter(o => isCreationType(o.type) && o.type.startsWith('element.')).toArray()).flatMap(o => o.softWaitFor || []));
            const removed = await db.outbox.where('targetKey').anyOf(targetKeys.filter(key => !originKeys.has(key)))
                .filter(o => o.type === 'element.update' && ((o.status === OP_STATUS.PENDING && !o.attempts) || o.status === OP_STATUS.FAILED))
                .toArray();
            if (removed.length) {
                await db.outbox.bulkDelete(removed.map(o => o.seq));
                notify();
            }
            return removed;
        },

        // Lit une opération
        get: (id) => byId(id).first(),
        // Toutes les opérations, dans l'ordre d'ajout
        list: () => db.outbox.orderBy('seq').toArray(),
        // Opérations d'une voie prêtes à partir maintenant, dans l'ordre
        listSendable: (lane, nowMs = Date.now()) => db.outbox.where('status').equals(OP_STATUS.PENDING)
            .filter(o => o.lane === lane && (o.nextAttemptAt || 0) <= nowMs)
            .sortBy('seq'),
        // Indique s'il reste une opération pour cet élément (en dehors de exceptId)
        hasPendingFor: async (targetKey, exceptId = null) =>
            (await db.outbox.where('touches').equals(targetKey).filter(o => o.id !== exceptId).count()) > 0,
        // Heure de la prochaine tentative prévue (null si rien n'attend)
        nextAttemptTime: async () => {
            const pending = await db.outbox.where('status').equals(OP_STATUS.PENDING).toArray();
            return pending.length ? Math.min(...pending.map(o => o.nextAttemptAt || 0)) : null;
        },
        // Nombre d'opérations par statut
        counts: async () => {
            const counts = { pending: 0, sending: 0, failed: 0, blocked: 0, total: 0 };
            await db.outbox.each(o => {
                counts[o.status] = (counts[o.status] || 0) + 1;
                counts.total++;
            });
            return counts;
        },

        // Marque l'envoi en cours ; attempts > 0 interdit ensuite toute fusion ou annulation
        markSending: async (id) => {
            await byId(id).modify(o => {
                o.status = OP_STATUS.SENDING;
                o.attempts = (o.attempts || 0) + 1;
            });
            notify();
        },
        // Remet l'opération en attente
        markPending: (id) => update(id, { status: OP_STATUS.PENDING }),
        // Remet en attente jusqu'à nextAttemptAt, après une erreur temporaire
        markRetry: (id, error, nextAttemptAt, counters = {}) => update(id, { ...counters, status: OP_STATUS.PENDING, nextAttemptAt, lastError: { ...error, at: nowIso() } }),
        // Échec définitif, en attente d'une décision de l'utilisateur
        markFailed: (id, error) => update(id, { status: OP_STATUS.FAILED, lastError: { ...error, at: nowIso() } }),
        // Bloquée par une autre opération
        markBlocked: (id, reason) => update(id, { status: OP_STATUS.BLOCKED, lastError: { kind: 'blocked', reason, at: nowIso() } }),
        // Bloque les opérations qui attendent une opération en échec (sauf dépendance souple : elles partent sans elle)
        blockDependents: async (id) => {
            if ((await byId(id).first())?.softDependents) return;
            await db.outbox.filter(o => (o.dependsOn || []).includes(id) && o.status === OP_STATUS.PENDING)
                .modify({ status: OP_STATUS.BLOCKED, lastError: { kind: 'blocked', reason: 'dependency_failed', at: nowIso() } });
            notify();
        },
        // Relance manuelle : l'opération et celles qu'elle bloquait, de proche en proche, repartent ; compteurs d'erreurs remis à zéro
        requeue: async (id) => {
            await db.transaction('rw', db.outbox, async () => {
                await byId(id).modify({ status: OP_STATUS.PENDING, nextAttemptAt: 0, lastError: null, serverErrors: 0, unanswered: 0 });
                const queue = [id];
                while (queue.length) {
                    const currentId = queue.shift();
                    const blocked = await db.outbox.filter(o => (o.dependsOn || []).includes(currentId) && o.status === OP_STATUS.BLOCKED).toArray();
                    for (const o of blocked) {
                        await byId(o.id).modify({ status: OP_STATUS.PENDING, lastError: null });
                        queue.push(o.id);
                    }
                }
            });
            notify();
        },
        // Retire une opération (acceptée ou écartée)
        remove: async (id) => {
            await byId(id).delete();
            notify();
        },
        // Abandon : retire l'opération et toutes celles qui en dépendent
        // Seules partent les saisies qui ont besoin de ce que l'opération crée
        removeWithDependents: async (id) => {
            const removed = [];
            await db.transaction('rw', db.outbox, async () => {
                // Saisie débloquée : celles qu'elle bloquait repartent aussi, de proche en proche (comme requeue)
                const unblock = async (ids) => {
                    const pending = [...ids];
                    while (pending.length) {
                        const blocked = await byId(pending.shift()).first();
                        if (blocked?.status !== OP_STATUS.BLOCKED) continue;
                        await byId(blocked.id).modify({ status: OP_STATUS.PENDING, lastError: null });
                        pending.push(...(await db.outbox.filter(o => (o.dependsOn || []).includes(blocked.id) && o.status === OP_STATUS.BLOCKED).toArray()).map(o => o.id));
                    }
                };
                const queue = [id];
                while (queue.length) {
                    const currentId = queue.shift();
                    const op = await byId(currentId).first();
                    if (!op) continue;
                    removed.push(op);
                    await byId(currentId).delete();
                    if (op.softDependents) continue;
                    const dependents = await db.outbox.filter(o => (o.dependsOn || []).includes(currentId)).toArray();
                    // Opération qui ne crée rien (modification, suppression…) : ses dépendantes sont seulement débloquées
                    if (!isCreationType(op.type)) {
                        await unblock(dependents.map(o => o.id));
                        continue;
                    }
                    // Entités créées : une dépendante ne part que pour la partie qui les vise ; la suppression des origines d'une
                    // découpe part avec le morceau, l'origine resterait sinon supprimée sans lui
                    const createdKeys = op.type === 'element.bulkCreate' ? op.payload.features.map(feature => `element:${feature.id}`)
                        : ['element.create', 'photo.upload'].includes(op.type) ? [op.targetKey]
                            : op.type === 'occurrences.create' ? op.touches : null;
                    const createdIds = (createdKeys || []).filter(key => key.startsWith('element:')).map(key => key.slice('element:'.length));
                    for (const dependent of dependents) {
                        const lostKeys = [...(createdKeys || []), ...(dependent.type === 'element.delete' && op.type.startsWith('element.') ? op.softWaitFor || [] : [])];
                        // Autre paquet du même lot de créations : tout ou rien
                        const sameBatch = dependent.touches.some(key => key.startsWith('batch:') && op.touches.includes(key));
                        if (!createdKeys || sameBatch) {
                            queue.push(dependent.id);
                            continue;
                        }
                        // Ne vise que l'origine d'une découpe (photo, modification) : seulement débloquée
                        if (![...dependent.touches, ...dependent.refs.map(targetKeyOf)].some(key => lostKeys.includes(key))) {
                            await byId(dependent.id).modify({ dependsOn: dependent.dependsOn.filter(dependencyId => dependencyId !== currentId) });
                            await unblock([dependent.id]);
                            continue;
                        }
                        if (dependent.type !== 'occurrences.create') {
                            const reduced = withoutEntities(dependent, lostKeys);
                            if (!reduced) {
                                queue.push(dependent.id);
                                continue;
                            }
                            await byId(dependent.id).modify({ ...reduced.kept, dependsOn: dependent.dependsOn.filter(dependencyId => dependencyId !== currentId) });
                            await unblock([dependent.id]);
                            if (reduced.lost) removed.push(reduced.lost);
                            continue;
                        }
                        const lostElementIds = dependent.payload.creation.elements.map(element => element.elementId).filter(elementId => createdIds.includes(elementId));
                        // Plus aucun élément de cette création dans le lot (occurrence retirée avant) : seulement débloqué
                        if (!lostElementIds.length) {
                            await byId(dependent.id).modify({ dependsOn: dependent.dependsOn.filter(dependencyId => dependencyId !== currentId) });
                            await unblock([dependent.id]);
                            continue;
                        }
                        if (lostElementIds.length === dependent.payload.creation.elements.length) {
                            queue.push(dependent.id);
                            continue;
                        }
                        // Lot d'actions dont seuls certains éléments venaient de cette création : seules leurs occurrences partent, avec ce qui ne
                        // concerne qu'elles (validations, photos) ; une copie réduite du lot est rendue pour l'annulation à l'écran
                        const lostOccurrenceKeys = lostElementIds.map(elementId => `occurrence:${dependent.payload.localIds[elementId]}`);
                        const localIds = { ...dependent.payload.localIds };
                        lostElementIds.forEach(elementId => delete localIds[elementId]);
                        await byId(dependent.id).modify({
                            touches: dependent.touches.filter(key => !lostOccurrenceKeys.includes(key)),
                            refs: dependent.refs.filter(ref => !lostElementIds.includes(ref.id)),
                            dependsOn: dependent.dependsOn.filter(dependencyId => dependencyId !== currentId),
                            payload: { ...dependent.payload, localIds, creation: { ...dependent.payload.creation, elements: dependent.payload.creation.elements.filter(element => !lostElementIds.includes(element.elementId)) } }
                        });
                        await unblock([dependent.id]);
                        removed.push({ ...dependent, payload: { ...dependent.payload, localIds: Object.fromEntries(lostElementIds.map(elementId => [elementId, dependent.payload.localIds[elementId]])) } });
                        const onlyLost = await db.outbox.filter(o => o.id !== dependent.id && ((o.touches.length && o.touches.every(key => lostOccurrenceKeys.includes(key))) || lostOccurrenceKeys.includes(o.cascadeKey))).toArray();
                        queue.push(...onlyLost.map(o => o.id));
                    }
                }
            });
            notify();
            return removed;
        },
        // Opération restée « en cours d'envoi » (onglet fermé pendant l'envoi) remise en attente, d'une voie ou de toutes ;
        // la clé d'idempotence évite les doublons
        resetSending: async (lane = null) => {
            await db.outbox.where('status').equals(OP_STATUS.SENDING).filter(o => !lane || o.lane === lane).modify({ status: OP_STATUS.PENDING });
            notify();
        },
        // Retour du réseau : les reprises après une coupure repartent tout de suite
        resetRetryDelays: async () => {
            await db.outbox.where('status').equals(OP_STATUS.PENDING)
                .filter(o => (o.nextAttemptAt || 0) > 0 && ['network', 'timeout'].includes(o.lastError?.reason))
                .modify({ nextAttemptAt: 0 });
        }
    };
    return outbox;
};
